import { Badge } from '@/components/ui/Badge';
import { API_BASE_URL, api } from '@/lib/api';
import { cn, formatCurrency, formatEventDate, formatSeatIdentifier } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileDown,
  FileText,
  Hourglass,
  Search,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

interface Purchase {
  id: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  payment_status: 'pending' | 'confirmed' | 'failed' | 'cancelled' | string;
  payment_proof_url: string | null;
  created_at: string;
  updated_at: string;
  // Nuevos campos de timestamp para tracking de estados
  payment_proof_uploaded_at?: string | null;
  payment_confirmed_at?: string | null;
  payment_rejected_at?: string | null;
  events: {
    id: string;
    name: string;
    event_date: string;
    venue_name: string;
  };
  tickets: Array<{
    id: string;
    ticket_code: string;
    seats: {
      seat_identifier: string;
    };
  }>;
}

type PurchaseStatusFilter = 'all' | 'pending' | 'confirmed' | 'failed' | 'cancelled';

function getOrderNumber(purchaseId: string) {
  return `#${purchaseId.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

function formatPurchaseDateShort(dateString: string) {
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(dateString));
}

function formatPurchaseDateTime(dateString: string | null | undefined) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getPurchaseStatusBadge(status: string) {
  switch (status) {
    case 'confirmed':
      return {
        label: 'Confirmada',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
        variant: 'success' as const,
        textColor: 'text-emerald-700',
        bgColor: 'bg-emerald-50',
      };
    case 'pending':
      return {
        label: 'Pendiente',
        icon: <Hourglass className="w-3.5 h-3.5" />,
        variant: 'pending' as const,
        textColor: 'text-amber-700',
        bgColor: 'bg-amber-50',
      };
    case 'failed':
      return {
        label: 'Rechazada',
        icon: <XCircle className="w-3.5 h-3.5" />,
        variant: 'danger' as const,
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
      };
    case 'cancelled':
      return {
        label: 'Cancelada',
        icon: <Ban className="w-3.5 h-3.5" />,
        variant: 'default' as const,
        textColor: 'text-gray-700',
        bgColor: 'bg-gray-100',
      };
    default:
      return {
        label: status,
        icon: <FileText className="w-3.5 h-3.5" />,
        variant: 'default' as const,
        textColor: 'text-gray-700',
        bgColor: 'bg-gray-100',
      };
  }
}

function EmptyDetails() {
  return (
    <div className="flex flex-col items-center justify-center h-full py-20 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 border border-gray-100 mb-4">
        <ShoppingBag className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900">Selecciona una compra</h3>
      <p className="mt-2 text-sm text-gray-500 max-w-[250px]">
        Haz clic en una transacción de la lista para ver todos los detalles aquí.
      </p>
    </div>
  );
}

function PurchaseDetails({ purchase }: { purchase: Purchase }) {
  const badge = getPurchaseStatusBadge(purchase.payment_status);
  const orderNumber = getOrderNumber(purchase.id);

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-50 rounded-lg">
            <ShoppingBag className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Orden {orderNumber}</h2>
            <div className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 mt-0.5 ${badge.bgColor} ${badge.textColor}`}>
              {badge.label}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Ticket Link / Actions */}
        <div className="flex flex-col gap-2">
          <div className={cn(
            "grid gap-2",
            purchase.payment_status === 'pending' && !purchase.payment_proof_url ? "grid-cols-2" : "grid-cols-1"
          )}>
            <Link to={`/compra/${purchase.id}`} className="w-full btn-primary flex items-center justify-center gap-2 py-2.5 text-sm">
              Ver detalles completos
            </Link>

            {purchase.payment_status === 'pending' && !purchase.payment_proof_url && (
              <Link
                to={`/compra/${purchase.id}`}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors shadow-sm"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>Subir Pago</span>
              </Link>
            )}
          </div>

          {(purchase.payment_status === 'confirmed' || purchase.payment_proof_url) && (
            <div className="grid grid-cols-2 gap-2">
              {purchase.payment_status === 'confirmed' && (
                <a
                  href={`${API_BASE_URL}/purchases/${purchase.id}/tickets`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  title="Descargar Tickets"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  <span>Tickets</span>
                </a>
              )}

              {purchase.payment_proof_url && (
                <a
                  href={purchase.payment_proof_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Comprobante</span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Purchase Details Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Detalles de compra</h3>
          </div>

          <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-4 space-y-4">
            <div className="flex justify-between items-start">
              <span className="text-xs text-gray-500">Evento</span>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{purchase.events.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{purchase.events.venue_name}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Fecha del evento</span>
              <span className="text-xs font-medium text-gray-900">
                {formatEventDate(purchase.events.event_date, { includeTime: true, shortFormat: true })}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Asientos</span>
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs font-medium text-gray-900">x{purchase.tickets.length}</span>
                <div className="flex flex-wrap justify-end gap-1 max-w-[150px]">
                  {purchase.tickets.map((t) => (
                    <span key={t.id} className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-[10px] font-medium text-indigo-700 border border-indigo-100">
                      {formatSeatIdentifier(t.seats.seat_identifier)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Total</span>
              <span className="text-sm font-bold text-gray-900">{formatCurrency(purchase.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Línea de tiempo</h3>
          <div className="relative pl-4 border-l border-gray-200 space-y-6">

            {/* 1. Orden Creada + 1.1 Esperando Pago */}
            <div className="relative">
              <div className={cn(
                "absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ring-1",
                purchase.payment_status === 'pending' && !purchase.payment_proof_url
                  ? "bg-amber-500 ring-amber-100"
                  : "bg-gray-300 ring-gray-200"
              )} />
              <p className="text-xs text-gray-500 mb-0.5">{formatPurchaseDateTime(purchase.created_at)}</p>
              <p className="text-sm font-medium text-gray-900">Orden Creada</p>

              {/* 1.1 Esperando Pago (integrado en el mismo bloque) */}
              {purchase.payment_status === 'pending' && !purchase.payment_proof_url && (
                <div className="bg-amber-50/50 rounded-md p-2 mt-2 -ml-1">
                  <p className="text-sm font-medium text-amber-900">Esperando Pago</p>
                  <p className="text-xs text-amber-700">Por favor sube tu comprobante para continuar</p>
                </div>
              )}

              {/* Texto normal si ya pasó este estado */}
              {!(purchase.payment_status === 'pending' && !purchase.payment_proof_url) && (
                <p className="text-xs text-gray-500">La orden fue generada correctamente</p>
              )}
            </div>

            {/* 2. Comprobante Subido - En Espera de Confirmación */}
            {purchase.payment_proof_url && (
              <div className="relative">
                <div className={cn(
                  "absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ring-1",
                  purchase.payment_status === 'pending'
                    ? "bg-blue-500 ring-blue-100"
                    : "bg-gray-300 ring-gray-200"
                )} />
                <p className="text-xs text-gray-500 mb-0.5">
                  {/* Usar timestamp específico o fallback a updated_at */}
                  {formatPurchaseDateTime(purchase.payment_proof_uploaded_at || purchase.updated_at || purchase.created_at)}
                </p>
                <p className="text-sm font-medium text-gray-900">Comprobante Subido</p>

                {purchase.payment_status === 'pending' && (
                  <div className="bg-blue-50/50 rounded-md p-2 mt-2 -ml-1">
                    <p className="text-sm font-medium text-blue-900">En Espera de Confirmación</p>
                    <p className="text-xs text-blue-700">Tu comprobante está siendo revisado</p>
                  </div>
                )}

                {purchase.payment_status !== 'pending' && (
                  <p className="text-xs text-gray-500">Comprobante enviado para revisión</p>
                )}
              </div>
            )}

            {/* 3. Comprobante Aceptado (solo si está confirmado) */}
            {purchase.payment_status === 'confirmed' && (
              <div className="relative">
                <div className="absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white ring-1 ring-emerald-100" />
                <p className="text-xs text-gray-500 mb-0.5">
                  {/* Usar timestamp específico o fallback a updated_at */}
                  {formatPurchaseDateTime(purchase.payment_confirmed_at || purchase.updated_at || purchase.created_at)}
                </p>
                <p className="text-sm font-medium text-gray-900">Comprobante Aceptado</p>
                <p className="text-xs text-gray-500">El comprobante fue validado exitosamente</p>
              </div>
            )}

            {/* 4. Pago Confirmado (solo si está confirmado) */}
            {purchase.payment_status === 'confirmed' && (
              <div className="relative">
                <div className="absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white ring-1 ring-emerald-100" />
                <p className="text-xs text-gray-500 mb-0.5">
                  {/* Usar timestamp específico o fallback a updated_at */}
                  {formatPurchaseDateTime(purchase.payment_confirmed_at || purchase.updated_at || purchase.created_at)}
                </p>
                <div className="bg-emerald-50/50 rounded-md p-2 mt-1 -ml-1">
                  <p className="text-sm font-medium text-emerald-900">Pago Confirmado</p>
                  <p className="text-xs text-emerald-700">¡Todo listo! Tu pago ha sido procesado</p>
                </div>
              </div>
            )}

            {/* Caso Rechazado (reemplaza los pasos 3 y 4) */}
            {purchase.payment_status === 'failed' && (
              <div className="relative">
                <div className="absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white ring-1 ring-red-100" />
                <p className="text-xs text-gray-500 mb-0.5">
                  {/* Usar timestamp específico o fallback a updated_at */}
                  {formatPurchaseDateTime(purchase.payment_rejected_at || purchase.updated_at || purchase.created_at)}
                </p>
                <div className="bg-red-50/50 rounded-md p-2 mt-1 -ml-1">
                  <p className="text-sm font-medium text-red-900">Pago Rechazado</p>
                  <p className="text-xs text-red-700">Tu comprobante no es válido o hubo un problema</p>
                </div>
              </div>
            )}

            {/* Caso Cancelado */}
            {purchase.payment_status === 'cancelled' && (
              <div className="relative">
                <div className="absolute -left-[21px] top-0.5 w-2.5 h-2.5 rounded-full bg-gray-500 border-2 border-white ring-1 ring-gray-100" />
                <p className="text-xs text-gray-500 mb-0.5">
                  {formatPurchaseDateTime(purchase.updated_at || purchase.created_at)}
                </p>
                <div className="bg-gray-100 rounded-md p-2 mt-1 -ml-1">
                  <p className="text-sm font-medium text-gray-900">Orden Cancelada</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Details Section */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Detalles de pago</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Método</span>
              <div className="flex items-center gap-2">
                <CreditCard className="w-3 5 h-3.5 text-gray-400" />
                <span className="font-medium text-gray-900">Transferencia / Depósito</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total Precio</span>
              <span className="font-medium text-gray-900">{formatCurrency(purchase.total_amount)}</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-base font-semibold text-gray-900">Total</span>
            <span className="text-lg font-bold text-gray-900">{formatCurrency(purchase.total_amount)}</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function MyPurchasesPage() {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedPurchaseId, setSelectedPurchaseId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PurchaseStatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // Reduced slightly to fit better

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPurchases = async () => {
      try {
        setIsLoading(true);
        const data = await api.get<Purchase[]>('/purchases/my-purchases');
        setPurchases(data);
        // Select the first one by default if available for a better "dashboard" feel
        // if (data.length > 0) setSelectedPurchaseId(data[0].id);
      } catch (err) {
        console.error('Error fetching purchases:', err);
        setError('No se pudieron cargar tus compras');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchases();
  }, [isAuthenticated]);

  const counts = useMemo(() => {
    const base = {
      all: purchases.length,
      pending: 0,
      confirmed: 0,
      failed: 0,
      cancelled: 0,
    };

    for (const purchase of purchases) {
      if (purchase.payment_status === 'pending') base.pending++;
      if (purchase.payment_status === 'confirmed') base.confirmed++;
      if (purchase.payment_status === 'failed') base.failed++;
      if (purchase.payment_status === 'cancelled') base.cancelled++;
    }

    return base;
  }, [purchases]);

  const filteredPurchases = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let result = [...purchases];

    if (statusFilter !== 'all') {
      result = result.filter((purchase) => purchase.payment_status === statusFilter);
    }

    if (query) {
      result = result.filter((purchase) => {
        const orderNumber = getOrderNumber(purchase.id).toLowerCase();
        const eventName = purchase.events?.name?.toLowerCase() ?? '';
        const venueName = purchase.events?.venue_name?.toLowerCase() ?? '';

        // Búsqueda por monto (normalizamos la consulta y el monto)
        const amountStr = purchase.total_amount.toString();
        const formattedAmount = formatCurrency(purchase.total_amount).toLowerCase();
        // Limpiamos la consulta de símbolos de moneda y comas para búsqueda numérica pura
        const cleanQuery = query.replace(/[$,]/g, '').trim();

        return (
          orderNumber.includes(query) ||
          eventName.includes(query) ||
          venueName.includes(query) ||
          amountStr.includes(cleanQuery) ||
          formattedAmount.includes(query)
        );
      });
    }

    result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return result;
  }, [purchases, searchQuery, statusFilter]);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(filteredPurchases.length / pageSize);
    return Math.max(1, pages);
  }, [filteredPurchases.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const paginatedPurchases = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPurchases.slice(start, start + pageSize);
  }, [filteredPurchases, currentPage]);

  const selectedPurchase = useMemo(() => {
    if (!selectedPurchaseId) return null;
    return purchases.find((purchase) => purchase.id === selectedPurchaseId) ?? null;
  }, [purchases, selectedPurchaseId]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/mis-compras' }} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Compras</h1>
          <p className="text-sm text-gray-500">Gestiona tus tickets y pagos</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-700 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Filters & Actions Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2 mb-6 flex flex-col md:flex-row items-center gap-2">
        {/* Search */}
        <div className="relative w-full md:w-64 lg:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar transacción..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-transparent border-none focus:ring-0 placeholder:text-gray-400 text-gray-900 outline-none"
          />
        </div>

        <div className="h-6 w-px bg-gray-200 hidden md:block" />

        {/* Pills Filters */}
        <div className="flex-1 w-full overflow-x-auto no-scrollbar py-1">
          <div className="flex items-center gap-1.5 min-w-max px-2">
            {[
              { id: 'all', label: 'Todas', count: counts.all },
              { id: 'pending', label: 'Pendientes', count: counts.pending },
              { id: 'confirmed', label: 'Confirmadas', count: counts.confirmed },
              { id: 'failed', label: 'Rechazadas', count: counts.failed },
              { id: 'cancelled', label: 'Canceladas', count: counts.cancelled },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setStatusFilter(filter.id as PurchaseStatusFilter)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border cursor-pointer",
                  statusFilter === filter.id
                    ? "bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm"
                    : "bg-white border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-200"
                )}
              >
                {filter.label} <span className="opacity-70 ml-0.5">({filter.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left List */}
        <div className="lg:col-span-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* List Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50/50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-12 md:col-span-5 text-center">Transacción</div>
            <div className="col-span-2 text-center">Monto</div>
            <div className="col-span-3 text-center">Fecha</div>
            <div className="col-span-2 text-center">Status</div>
          </div>

          <div className="divide-y divide-gray-50 min-h-[400px]">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : paginatedPurchases.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-gray-900 font-medium">No se encontraron compras</h3>
                <p className="text-gray-500 text-sm mt-1">Intenta ajustar tus filtros de búsqueda</p>
              </div>
            ) : (
              paginatedPurchases.map((purchase) => {
                const isSelected = selectedPurchaseId === purchase.id;
                const badge = getPurchaseStatusBadge(purchase.payment_status);

                return (
                  <div
                    key={purchase.id}
                    onClick={() => setSelectedPurchaseId(purchase.id)}
                    className={cn(
                      "group cursor-pointer transition-all duration-200 hover:bg-gray-50",
                      isSelected && "bg-indigo-50/40 hover:bg-indigo-50/60"
                    )}
                  >
                    <div className="p-4 md:px-6 md:py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                      {/* Transaction Info */}
                      <div className="md:col-span-5 flex items-center gap-3 md:gap-4 overflow-hidden">
                        <div className={cn(
                          "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border transition-colors",
                          isSelected ? "bg-indigo-100 border-indigo-200 text-indigo-600" : "bg-gray-100 border-gray-200 text-gray-500 group-hover:bg-white group-hover:border-indigo-100 group-hover:text-indigo-600"
                        )}>
                          <ShoppingBag className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className={cn("text-sm font-semibold truncate transition-colors", isSelected ? "text-indigo-900" : "text-gray-900")}>
                            {purchase.events.name}
                          </h4>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            Orden {getOrderNumber(purchase.id)}
                          </p>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="md:col-span-2 flex items-center justify-between md:justify-center">
                        <span className="md:hidden text-xs text-gray-500 font-medium">Monto:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(purchase.total_amount)}</span>
                      </div>

                      {/* Date */}
                      <div className="md:col-span-3 flex items-center justify-between md:justify-center">
                        <span className="md:hidden text-xs text-gray-500 font-medium">Fecha:</span>
                        <div className="flex flex-col md:items-center">
                          <span className="text-sm text-gray-600">{formatPurchaseDateShort(purchase.created_at)}</span>
                          <span className="text-xs text-gray-400 hidden md:inline-block">
                            {new Date(purchase.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="md:col-span-2 flex items-center justify-between md:justify-center">
                        <Badge
                          variant={badge.variant}
                          className="rounded-full px-2.5 py-0.5 text-xs font-medium border-0"
                        >
                          {badge.label}
                        </Badge>
                        <ChevronRight className={cn("w-4 h-4 text-gray-300 md:hidden transition-transform", isSelected && "rotate-90")} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {filteredPurchases.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
              <span className="text-xs text-gray-500">
                Mostrando {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredPurchases.length)} de {filteredPurchases.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent transition-colors text-gray-500 cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-700">Página {currentPage}</span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent transition-colors text-gray-500 cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Detail Panel */}
        <div className="hidden lg:block lg:col-span-4 transition-all duration-300 sticky top-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[500px] flex flex-col relative overflow-hidden">
            {selectedPurchase ? (
              <PurchaseDetails purchase={selectedPurchase} />
            ) : (
              <EmptyDetails />
            )}
          </div>
        </div>

        {/* Mobile Detail Panel as a lower section or overlay if needed - keeping it as is for now, but enabling visibility of selected on mobile below list could be good, or just rely on navigating to detail page. 
          The original code had a mobile detail section below the list. I removed it because the user explicitly asked for "Panel de detalle a la derecha" and list improvements.
          For mobile, clicking usually navigates or opens a modal. 
          I'll add a mobile-only section at the bottom if a purchase is selected, to ensure usability.
        */}
        {selectedPurchase && (
          <div className="lg:hidden col-span-1 mt-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <PurchaseDetails purchase={selectedPurchase} />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
